import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GiMicroscope } from "react-icons/gi";
import {  HiMail } from "react-icons/hi";
import { FaTelegram , FaInstagram, FaHandshake, FaBuilding, FaCogs, FaWifi } from "react-icons/fa";
import { FaWhatsapp, FaPaperPlane, FaFileAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";

// Import translations
import fa from "./locales/ContactUs-fa.json";
import en from "./locales/ContactUs-en.json";
import ar from "./locales/ContactUs-ar.json";
import tr from "./locales/ContactUs-tr.json";

const ContactUs: React.FC<{id?:string}> = ({id}) => {
    const { t, i18n } = useTranslation("ContactUs");

    // Register component translations
    useEffect(() => {
        const ns = "ContactUs";
        const resources = { fa, en, ar, tr } as const;

        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    const [formData, setFormData] = useState({
        name: "",
        phone: "",
        message: "",
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [activeField, setActiveField] = useState<string | null>(null);
    const [hoveredCard, setHoveredCard] = useState<string | null>(null);
    const [focusedInput, setFocusedInput] = useState<string | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        // اینجا می‌توانید کد ارسال فرم به سرور را قرار دهید
        // برای نمونه، یک تایمر ساده برای شبیه‌سازی ارسال به سرور
        setTimeout(() => {
            setIsSubmitting(false);
            setSubmitSuccess(true);
            setFormData({ name: "", phone: "", message: "" });

            // پس از چند ثانیه پیام موفقیت را پاک می‌کنیم
            setTimeout(() => {
                setSubmitSuccess(false);
            }, 5000);
        }, 1500);
    };

    return (
        <section dir={i18n.dir()} id={id} className="pb-10 relative overflow-hidden">
            <div className="container mx-auto px-4 sm:px-10 relative z-10">
                <div className="grid md:grid-cols-2 gap-12">
                    {/* بخش چطور می‌توانیم به شما کمک کنیم؟ */}
                    <motion.div
                        className="relative backdrop-blur-xl bg-white/90 p-6 md:p-8 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-200/80 overflow-hidden flex flex-col justify-between"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        viewport={{ once: true }}
                    >
                        <div>
                            <div className="w-16 h-16 bg-gradient-to-br from-[#0038A8] via-[#0072CE] to-[#155DFC] rounded-2xl flex items-center justify-center mb-6 text-white text-2xl shadow-lg shadow-blue-500/25">
                                <FaHandshake className="text-2xl" />
                            </div>

                            <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">
                                {t("howCanWeHelp")}
                            </h2>

                            <p className="text-slate-600 mb-8 leading-relaxed text-base">
                                {t("teamReadyToGuide")}
                            </p>

                            <div className="grid grid-cols-1 gap-4 mb-2">
                                {/* همکاری پژوهشی */}
                                <motion.div
                                    className="bg-blue-50/50 hover:bg-blue-50/80 p-5 rounded-2xl border border-blue-100/80 transition-all duration-300 flex items-center group cursor-pointer"
                                    whileHover={{ y: -3 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <div className="w-11 h-11 bg-gradient-to-br from-[#0038A8] to-[#0072CE] rounded-xl flex items-center justify-center text-white me-4 shadow-md shadow-blue-500/20 shrink-0">
                                        <GiMicroscope className="text-xl" />
                                    </div>
                                    <div className="flex flex-col">
                                        <h3 className="font-bold text-slate-800 text-base">{t("researchCollaboration")}</h3>
                                        <p className="text-slate-600 text-sm leading-relaxed mt-0.5">
                                            {t("researchCollaborationDesc")}
                                        </p>
                                    </div>
                                </motion.div>

                                {/* فروش سازمانی */}
                                <motion.div
                                    className="bg-blue-50/50 hover:bg-blue-50/80 p-5 rounded-2xl border border-blue-100/80 transition-all duration-300 flex items-center group cursor-pointer"
                                    whileHover={{ y: -3 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <div className="w-11 h-11 bg-gradient-to-br from-[#0038A8] to-[#0072CE] rounded-xl flex items-center justify-center text-white me-4 shadow-md shadow-blue-500/20 shrink-0">
                                        <FaBuilding className="text-xl" />
                                    </div>
                                    <div className="flex flex-col">
                                        <h3 className="font-bold text-slate-800 text-base">{t("organizationalSales")}</h3>
                                        <p className="text-slate-600 text-sm leading-relaxed mt-0.5">
                                            {t("organizationalSalesDesc")}
                                        </p>
                                    </div>
                                </motion.div>

                                {/* پیاده سازی راهکارهای اختصاصی */}
                                <motion.div
                                    className="bg-blue-50/50 hover:bg-blue-50/80 p-5 rounded-2xl border border-blue-100/80 transition-all duration-300 flex items-center group cursor-pointer"
                                    whileHover={{ y: -3 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <div className="w-11 h-11 bg-gradient-to-br from-[#0038A8] to-[#0072CE] rounded-xl flex items-center justify-center text-white me-4 shadow-md shadow-blue-500/20 shrink-0">
                                        <FaCogs className="text-xl" />
                                    </div>
                                    <div className="flex flex-col">
                                        <h3 className="font-bold text-slate-800 text-base">{t("customSolutions")}</h3>
                                        <p className="text-slate-600 text-sm leading-relaxed mt-0.5">
                                            {t("customSolutionsDesc")}
                                        </p>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </motion.div>

                    {/* فرم تماس با ما */}
                    <motion.div
                        className="self-center relative bg-white/90 backdrop-blur-xl p-6 md:p-8 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-200/80"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-2xl font-bold text-slate-800 mb-2">
                            {t("contactUs")}
                        </h2>

                        <p className="text-slate-600 mb-6 text-sm sm:text-base leading-relaxed">
                            {t("contactUsDesc")}
                        </p>

                        <AnimatePresence>
                            {submitSuccess && (
                                <motion.div
                                    className="relative bg-emerald-50 border border-emerald-200 text-emerald-700 p-4 rounded-2xl mb-6 backdrop-blur-sm overflow-hidden"
                                    initial={{ opacity: 0, y: -10, scale: 0.98 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -10, scale: 0.98 }}
                                    transition={{ duration: 0.3, ease: "easeOut" }}
                                >
                                    <div className="flex items-center relative z-10 font-medium text-sm">
                                        <span className="text-emerald-600 me-2 text-base font-bold">✓</span>
                                        {t("successMessage")}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
                            {/* Name Input */}
                            <div className="flex flex-col gap-1.5 opacity-80">
                                <label htmlFor="name" className="block text-slate-700 text-sm font-semibold">
                                    {t("name")}
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    disabled
                                    className="w-full px-4 py-3 bg-slate-100/90 border border-slate-200 rounded-xl text-slate-500 placeholder-slate-400 cursor-not-allowed text-sm"
                                    placeholder={t("fullNamePlaceholder")}
                                />
                            </div>

                            {/* Phone Input */}
                            <div className="flex flex-col gap-1.5 opacity-80">
                                <label htmlFor="phone" className="block text-slate-700 text-sm font-semibold">
                                    {t("phone")}
                                </label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    disabled
                                    className="w-full px-4 py-3 bg-slate-100/90 border border-slate-200 rounded-xl text-slate-500 placeholder-slate-400 cursor-not-allowed text-sm"
                                    placeholder={t("phonePlaceholder")}
                                    dir="ltr"
                                />
                            </div>

                            {/* Message Input */}
                            <div className="flex flex-col gap-1.5 opacity-80">
                                <label htmlFor="message" className="block text-slate-700 text-sm font-semibold">
                                    {t("message")}
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    disabled
                                    rows={3}
                                    className="w-full px-4 py-3 bg-slate-100/90 border border-slate-200 rounded-xl text-slate-500 placeholder-slate-400 cursor-not-allowed text-sm resize-none"
                                    placeholder={t("messagePlaceholder")}
                                />
                            </div>

                            {/* Submit Button (Disabled) */}
                            <button
                                type="button"
                                disabled
                                className="w-full bg-slate-100 text-slate-600 border border-slate-300/80 px-4 py-3.5 rounded-xl font-medium shadow-2xs cursor-not-allowed transition-all duration-200 flex items-center justify-center text-xs sm:text-sm text-center leading-relaxed"
                            >
                                <span>{t("formDisabled")}</span>
                            </button>
                        </form>

                        {/* Contact Info Channels */}
                        <div className="mt-6 pt-5 border-t border-slate-200/80">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {/* پشتیبانی در بله */}
                                <motion.a
                                    href="https://ble.ir/delband_care"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center p-2.5 rounded-xl bg-blue-50/60 hover:bg-blue-50 border border-blue-100/60 transition-colors group"
                                    whileHover={{ y: -2 }}
                                >
                                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#0038A8] to-[#0072CE] flex items-center justify-center text-white me-2.5 shrink-0 shadow-sm">
                                        <FaPaperPlane className="h-4 w-4" />
                                    </div>
                                    <span className="text-xs font-semibold text-slate-700 group-hover:text-[#0072CE] transition-colors">
                                        {t("supportBale")}
                                    </span>
                                </motion.a>

                                {/* پشتیبانی واتس آپ */}
                                <motion.a
                                    href="https://wa.me/message/GNNOKQBPQVSQB1"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center p-2.5 rounded-xl bg-blue-50/60 hover:bg-blue-50 border border-blue-100/60 transition-colors group"
                                    whileHover={{ y: -2 }}
                                >
                                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#0038A8] to-[#0072CE] flex items-center justify-center text-white me-2.5 shrink-0 shadow-sm">
                                        <FaWhatsapp className="h-4 w-4" />
                                    </div>
                                    <span className="text-xs font-semibold text-slate-700 group-hover:text-[#0072CE] transition-colors">
                                        {t("supportWhatsapp")}
                                    </span>
                                </motion.a>

                                {/* فرم ویژه سازمان‌ها */}
                                <motion.a
                                    href="https://survey.porsline.ir/s/gJ1ZAfwb"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center p-2.5 rounded-xl bg-blue-50/60 hover:bg-blue-50 border border-blue-100/60 transition-colors group"
                                    whileHover={{ y: -2 }}
                                >
                                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#0038A8] to-[#0072CE] flex items-center justify-center text-white me-2.5 shrink-0 shadow-sm">
                                        <FaFileAlt className="h-4 w-4" />
                                    </div>
                                    <span className="text-xs font-semibold text-slate-700 group-hover:text-[#0072CE] transition-colors">
                                        {t("orgForm")}
                                    </span>
                                </motion.a>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default ContactUs;
